import { LineItemType } from "../types/line-items";
import {
  OptionChoice,
  OptionsType,
  OrderLineChoice,
  SelectedLineOptions,
  SelectedOptions,
} from "../types/option";
import { Locale } from "../types/translations";
import { formatMoney } from "./currency";

type CheckboxOptions = {
  enabled: boolean;
  alternateChoice: OptionChoice;
};

const basketFilter = (lineItem: LineItemType): boolean => {
  if (lineItem.parentId) {
    return false;
  }
  return true;
};

const findSelectedChoice = (
  choices: OptionChoice[],
): OptionChoice | undefined => {
  const selected = choices.find((choice) => choice.selected && !choice.default);
  return selected;
};

const getSelectedOptions = (options: OptionsType[]): SelectedOptions[] =>
  options.reduce((allOptions: SelectedOptions[], option) => {
    if (option) {
      const selectedChoice = findSelectedChoice(option.choices);
      if (selectedChoice)
        allOptions.push({
          option: {
            optionId: option.id,
            name: option.name,
          },
          choices: {
            choiceId: selectedChoice.id,
            name: selectedChoice.name,
            price: selectedChoice.price,
          },
        });
    }
    return allOptions;
  }, []);

const getOptionsForOrder = (
  selectedOptions: SelectedOptions[],
): OrderLineChoice[] =>
  selectedOptions.reduce((options: OrderLineChoice[], option) => {
    if (option && option.choices?.choiceId) {
      options.push({
        optionId: option.option.optionId,
        choiceId: option.choices?.choiceId,
      });
    }
    return options;
  }, []);

const compareOptionsList = (
  list1: SelectedOptions[],
  list2: SelectedOptions[],
): SelectedOptions | boolean => {
  if (list1.length !== list2.length) {
    return false;
  }
  return list1.reduce((match: boolean, option1) => {
    const findInList2 = list2.find(
      (option2) =>
        option2.option.optionId === option1.option.optionId &&
        option2.choices?.choiceId === option1.choices?.choiceId,
    );
    return match && !!findInList2;
  }, true);
};

const findOptionInList = (
  optionList: SelectedLineOptions[],
  currentOptions: SelectedOptions[],
): SelectedLineOptions | undefined => {
  if (!optionList || optionList.length === 0) {
    return undefined;
  }
  return optionList.reduce(
    (
      selectedLineOption: SelectedLineOptions | undefined,
      currentLineOption: SelectedLineOptions,
    ) =>
      compareOptionsList(currentLineOption.options, currentOptions)
        ? currentLineOption
        : selectedLineOption,
    undefined,
  );
};

const getChoiceText = (item: SelectedLineOptions, locale: Locale): string => {
  let choiceText = item.options.reduce((accText, option, index) => {
    const ingredientPrice = option.choices?.price || 0;
    const price =
      ingredientPrice !== 0
        ? `(+${formatMoney(ingredientPrice, true, locale).amount})`
        : "";

    accText = `${accText}${option.choices?.name} ${price}`.trim();
    accText = index < item.options.length - 1 ? `${accText};` : accText;
    return accText;
  }, "");
  const quantityPrefix = item.quantity ? `${item.quantity} x ` : "";
  choiceText = `${quantityPrefix}${choiceText}`;
  return choiceText;
};

const getCheckboxOptions = (choices: OptionChoice[]): CheckboxOptions => {
  const [firstChoice, secondChoice] = choices;
  if (!secondChoice) {
    return { enabled: true, alternateChoice: firstChoice };
  }
  const additiveChoice = firstChoice.additive ? firstChoice : secondChoice;
  const defaultChoice = firstChoice.default ? firstChoice : secondChoice;

  const selectedChoice = firstChoice?.selected
    ? firstChoice
    : secondChoice?.selected
      ? secondChoice
      : undefined;

  let enabled = false;
  if (selectedChoice) {
    enabled = selectedChoice.additive;
  } else {
    enabled =
      additiveChoice?.selected ||
      (additiveChoice?.default && additiveChoice?.selected === undefined);
  }
  return {
    enabled,
    alternateChoice: getAlternateChoice(choices, defaultChoice, selectedChoice),
  };
};

const getAlternateChoice = (
  choices: OptionChoice[],
  defaultChoice: OptionChoice,
  selectedChoice?: OptionChoice,
) => {
  const [firstChoice, secondChoice] = choices;
  let alternateChoice = firstChoice;
  if (selectedChoice && selectedChoice === firstChoice) {
    alternateChoice = secondChoice;
  } else if (!selectedChoice && defaultChoice === firstChoice) {
    alternateChoice = secondChoice;
  }
  return alternateChoice;
};

export {
  basketFilter,
  getCheckboxOptions,
  getChoiceText,
  getSelectedOptions,
  getOptionsForOrder,
  findOptionInList,
};
